import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch } from 'react-redux';
import CreateButton from './CreateButton';
import PageNavigator from './PageNavigator';
import TicketDetails from './TicketDetails';
import CategoriesTabContent from './CategoriesTabContent';
import FilterDialog from './FilterDialog';
import { fetchPendingTickets, fetchClosedTickets, closeTicket, deleteTicket, exportTickets, searchCategories } from '../services/ticketService';
import { searchCustomers } from '../services/customerService';
import { showSuccessNotification, showErrorNotification } from '../store/slices/notificationSlice';
import { useCachedPermissions } from '../hooks/useCachedPermissions';
import styles from './TicketsTabContent.module.css';

const ITEMS_PER_PAGE = 16;

const TicketsTabContent = ({ selectedTab }) => {
  const dispatch = useDispatch();

  // Get tickets permissions
  const { hasDeleteTicket, hasCloseTicket, hasExportTicket } = useCachedPermissions();

  const [pendingTickets, setPendingTickets] = useState([]);
  const [closedTickets, setClosedTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 16,
    results: 0,
    last_page: 1
  });
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [exportLoading, setExportLoading] = useState(false);
  const [dataChanged, setDataChanged] = useState(false);

  // Filter related state
  const [showFilterDialog, setShowFilterDialog] = useState(false);
  const [filterValues, setFilterValues] = useState({});
  const [categorySearchResults, setCategorySearchResults] = useState([]);
  const [categorySearchLoading, setCategorySearchLoading] = useState(false);
  const [customerSearchResults, setCustomerSearchResults] = useState([]);
  const [customerSearchLoading, setCustomerSearchLoading] = useState(false);

  useEffect(() => {
    if (selectedTab === 'tickets-requests') {
      setCurrentPage(1);
      fetchPendingTicketsData(1);
    } else if (selectedTab === 'tickets-closed') {
      setCurrentPage(1);
      fetchClosedTicketsData(1);
    }
  }, [selectedTab]);

  // Reset selected ticket when sub-tab changes
  React.useEffect(() => {
    setSelectedTicketId(null);
    setCurrentPage(1);
    setSelectedItems([]);
    setSelectAll(false);
  }, [selectedTab]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdownId && !event.target.closest('[data-dropdown-menu]') && !event.target.closest(`.${styles.actionMenuButton}`)) {
        setOpenDropdownId(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [openDropdownId]);

  const fetchPendingTicketsData = async (page = currentPage) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchPendingTickets(page, ITEMS_PER_PAGE);
      if (response.data && response.data.data) {
        setPendingTickets(response.data.data);
        setPagination({
          total: response.data.total,
          page: response.data.page,
          limit: response.data.limit,
          results: response.data.results,
          last_page: response.data.last_page
        });
      } else {
        setPendingTickets([]);
        setPagination({
          total: 0,
          page: 1,
          limit: 16,
          results: 0,
          last_page: 1
        });
      }
    } catch (err) {
      setError(err.message);
      setPendingTickets([]);
      setPagination({
        total: 0,
        page: 1,
        limit: 16,
        results: 0,
        last_page: 1
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchClosedTicketsData = async (page = currentPage) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchClosedTickets(page, ITEMS_PER_PAGE);
      console.log('Closed tickets response:', response);
      if (response.data && response.data.data) {
        setClosedTickets(response.data.data);
        setPagination({
          total: response.data.total,
          page: response.data.page,
          limit: response.data.limit,
          results: response.data.results,
          last_page: response.data.last_page
        });
      } else {
        setClosedTickets([]);
        setPagination({
          total: 0,
          page: 1,
          limit: 16,
          results: 0,
          last_page: 1
        });
      }
    } catch (err) {
      console.error('Error fetching closed tickets:', err);
      setError(err.message);
      setClosedTickets([]);
      setPagination({
        total: 0,
        page: 1,
        limit: 16,
        results: 0,
        last_page: 1
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddTicket = () => {
    console.log('Create Ticket clicked');
    // Here you would typically navigate to add ticket form
  };

  // Debounce function for search
  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func.apply(null, args), delay);
    };
  };

  // Category search function
  const searchCategoriesFunction = async (searchTerm) => {
    // Don't search if term is empty or too short
    if (!searchTerm || searchTerm.trim().length < 2) {
      setCategorySearchResults([]);
      setCategorySearchLoading(false);
      return;
    }

    setCategorySearchLoading(true);
    try {
      const data = await searchCategories(searchTerm.trim());
      console.log('Category search response:', data);

      // Handle different response structures
      if (data && Array.isArray(data)) {
        setCategorySearchResults(data);
      } else if (data && data.data && Array.isArray(data.data)) {
        setCategorySearchResults(data.data);
      } else {
        setCategorySearchResults([]);
      }
    } catch (error) {
      console.error('Error searching categories:', error);
      setCategorySearchResults([]);
    } finally {
      setCategorySearchLoading(false);
    }
  };

  // Customer search function
  const searchCustomersFunction = async (searchTerm) => {
    // Don't search if term is empty or too short
    if (!searchTerm || searchTerm.trim().length < 2) {
      setCustomerSearchResults([]);
      setCustomerSearchLoading(false);
      return;
    }

    setCustomerSearchLoading(true);
    try {
      const response = await searchCustomers(searchTerm.trim());
      console.log('Customer search response:', response);

      // Handle different response structures
      if (response.data && Array.isArray(response.data)) {
        setCustomerSearchResults(response.data);
      } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
        setCustomerSearchResults(response.data.data);
      } else if (Array.isArray(response)) {
        setCustomerSearchResults(response);
      } else {
        setCustomerSearchResults([]);
      }
    } catch (error) {
      console.error('Error searching customers:', error);
      setCustomerSearchResults([]);
    } finally {
      setCustomerSearchLoading(false);
    }
  };

  // Debounced search functions
  const debouncedCategorySearch = debounce(searchCategoriesFunction, 300);
  const debouncedCustomerSearch = debounce(searchCustomersFunction, 300);

  // Filter fields configuration
  const filterFields = [
    {
      type: 'searchable-select',
      name: 'category',
      label: 'Category',
      placeholder: 'Search for a category...',
      onSearch: debouncedCategorySearch,
      searchOptions: categorySearchResults,
      loading: categorySearchLoading,
      displayKey: 'name',
      valueKey: '_id'
    },
    {
      type: 'searchable-select',
      name: 'customer',
      label: 'Customer',
      placeholder: 'Search by SWAG ID...',
      onSearch: debouncedCustomerSearch,
      searchOptions: customerSearchResults,
      loading: customerSearchLoading,
      displayKey: 'name',
      valueKey: '_id'
    },
    {
      type: 'date',
      name: 'date',
      label: 'Date',
      placeholder: 'Select date'
    }
  ];

  const handleFilter = () => {
    console.log('Filter button clicked - opening dialog');
    setShowFilterDialog(true);
  };

  const handleFilterApply = async (newFilterValues) => {
    console.log('Filter applied:', newFilterValues);
    setFilterValues(newFilterValues);
    setCurrentPage(1);

    // Call API with filter parameters
    await fetchTicketsWithFilters(newFilterValues, 1);
  };

  const fetchTicketsWithFilters = async (filters = filterValues, page = 1) => {
    setLoading(true);
    setError(null);

    try {
      // Prepare filter parameters
      const filterParams = {};

      if (filters.category && typeof filters.category === 'object') {
        filterParams.categoryId = filters.category._id;
      }

      if (filters.customer && typeof filters.customer === 'object') {
        filterParams.customerId = filters.customer._id;
      }

      if (filters.date) {
        filterParams.date = filters.date;
      }

      let response;
      if (selectedTab === 'tickets-requests') {
        response = await fetchPendingTickets(page, ITEMS_PER_PAGE, filterParams);
        if (response.data && response.data.data) {
          setPendingTickets(response.data.data);
          setPagination({
            total: response.data.total,
            page: response.data.page,
            limit: response.data.limit,
            results: response.data.results,
            last_page: response.data.last_page
          });
        } else {
          setPendingTickets([]);
          setPagination({
            total: 0,
            page: 1,
            limit: 16,
            results: 0,
            last_page: 1
          });
        }
      } else if (selectedTab === 'tickets-closed') {
        response = await fetchClosedTickets(page, ITEMS_PER_PAGE, filterParams);
        if (response.data && response.data.data) {
          setClosedTickets(response.data.data);
          setPagination({
            total: response.data.total,
            page: response.data.page,
            limit: response.data.limit,
            results: response.data.results,
            last_page: response.data.last_page
          });
        } else {
          setClosedTickets([]);
          setPagination({
            total: 0,
            page: 1,
            limit: 16,
            results: 0,
            last_page: 1
          });
        }
      }

      console.log('Filtered tickets response:', response);
    } catch (err) {
      console.error('Error fetching filtered tickets:', err);
      setError(err.message);
      if (selectedTab === 'tickets-requests') {
        setPendingTickets([]);
      } else if (selectedTab === 'tickets-closed') {
        setClosedTickets([]);
      }
      setPagination({
        total: 0,
        page: 1,
        limit: 16,
        results: 0,
        last_page: 1
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFilterClose = () => {
    setShowFilterDialog(false);
    // Clear search results when dialog closes
    setCategorySearchResults([]);
    setCustomerSearchResults([]);
  };

  const handleResetFilter = async () => {
    console.log('Reset filter clicked');
    setFilterValues({});
    setCurrentPage(1);
    setCategorySearchResults([]);
    setCustomerSearchResults([]);

    // Fetch data without filters
    if (selectedTab === 'tickets-requests') {
      await fetchPendingTicketsData(1);
    } else if (selectedTab === 'tickets-closed') {
      await fetchClosedTicketsData(1);
    }
  };

  // Check if there are active filters
  const hasActiveFilters = () => {
    return Object.keys(filterValues).some(key => {
      const value = filterValues[key];
      if (value === null || value === undefined || value === '') return false;
      if (typeof value === 'object' && value !== null) return true;
      return String(value).trim().length > 0;
    });
  };

  const handleExport = async () => {
    if (!hasExportTicket) {
      dispatch(showErrorNotification('You do not have permission to export tickets'));
      return;
    }

    setExportLoading(true);
    let status = 'pending';
    if (selectedTab === 'tickets-closed') status = 'closed';
    else if (selectedTab === 'tickets-requests') status = 'pending';
    try {
      await exportTickets(status);
      dispatch(showSuccessNotification('Tickets exported successfully!'));
    } catch (err) {
      dispatch(showErrorNotification(err.message || 'Failed to export tickets'));
    } finally {
      setExportLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusText = () => {
    if (selectedTab === 'tickets') return 'All';
    if (selectedTab === 'tickets-requests') return 'Requests';
    if (selectedTab === 'tickets-closed') return 'Closed';
    if (selectedTab === 'tickets-categories') return 'Categories';
    return 'All';
  };

  const getFilteredData = () => {
    let data;
    if (selectedTab === 'tickets') return [];
    if (selectedTab === 'tickets-requests') data = pendingTickets;
    if (selectedTab === 'tickets-closed') data = closedTickets;
    if (selectedTab === 'tickets-categories') return [];

    if (!data) return [];

    // With server-side pagination, return data as is (already paginated)
    return data;
  };

  const filteredData = getFilteredData();

  const handlePageChange = async (page) => {
    if (page >= 1 && page <= pagination.last_page) {
      setCurrentPage(page);

      // Fetch new page data
      if (hasActiveFilters()) {
        await fetchTicketsWithFilters(filterValues, page);
      } else {
        if (selectedTab === 'tickets-requests') {
          await fetchPendingTicketsData(page);
        } else if (selectedTab === 'tickets-closed') {
          await fetchClosedTicketsData(page);
        }
      }
    }
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedItems([]);
    } else {
      setSelectedItems(filteredData.map(item => item.id));
    }
    setSelectAll(!selectAll);
  };

  const handleSelectItem = (itemId) => {
    if (selectedItems.includes(itemId)) {
      setSelectedItems(selectedItems.filter(id => id !== itemId));
      setSelectAll(false);
    } else {
      setSelectedItems([...selectedItems, itemId]);
      if (selectedItems.length + 1 === filteredData.length) {
        setSelectAll(true);
      }
    }
  };

  const handleRowAction = (action, itemId) => {
    console.log(`${action} clicked for item:`, itemId);
    // Here you would implement row actions
  };

  const handleDropdownToggle = (e, ticketId) => {
    e.stopPropagation();

    if (openDropdownId === ticketId) {
      setOpenDropdownId(null);
      return;
    }

    // Calculate position for the dropdown
    const button = e.currentTarget;
    const rect = button.getBoundingClientRect();

    setDropdownPosition({
      top: rect.bottom + 4,
      left: rect.right - 160 // 160px is the dropdown width
    });

    setOpenDropdownId(ticketId);
  };

  const handleCloseDropdown = () => {
    setOpenDropdownId(null);
  };

  const handleCloseTicket = async (ticketId) => {
    if (!hasCloseTicket) {
      dispatch(showErrorNotification('You do not have permission to close tickets'));
      return;
    }

    setActionLoading(true);
    try {
      await closeTicket(ticketId);
      // Remove the ticket from local state immediately
      if (selectedTab === 'tickets-requests') {
        setPendingTickets(prev => prev.filter(ticket => ticket.id !== ticketId));
      } else if (selectedTab === 'tickets-closed') {
        setClosedTickets(prev => prev.filter(ticket => ticket.id !== ticketId));
      }
      setOpenDropdownId(null);
      setDataChanged(true);
      dispatch(showSuccessNotification('Ticket closed successfully!'));
    } catch (error) {
      console.error('Error closing ticket:', error);
      dispatch(showErrorNotification('Failed to close ticket: ' + error.message));
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteTicket = async (ticketId) => {
    if (!hasDeleteTicket) {
      dispatch(showErrorNotification('You do not have permission to delete tickets'));
      return;
    }

    if (!window.confirm('Are you sure you want to delete this ticket? This action cannot be undone.')) {
      return;
    }

    setActionLoading(true);
    try {
      await deleteTicket(ticketId);
      // Remove the ticket from local state immediately
      if (selectedTab === 'tickets-requests') {
        setPendingTickets(prev => prev.filter(ticket => ticket.id !== ticketId));
      } else if (selectedTab === 'tickets-closed') {
        setClosedTickets(prev => prev.filter(ticket => ticket.id !== ticketId));
      }
      setOpenDropdownId(null);
      setDataChanged(true);
      dispatch(showSuccessNotification('Ticket deleted successfully!'));
    } catch (error) {
      console.error('Error deleting ticket:', error);
      dispatch(showErrorNotification('Failed to delete ticket: ' + error.message));
    } finally {
      setActionLoading(false);
    }
  };

  const handleTicketClick = (ticketId) => {
    setSelectedTicketId(ticketId);
  };

  const handleCloseTicketDetails = () => {
    setSelectedTicketId(null);
    setDataChanged(false);
  };

  const handleCloseTicketDetailsWithRefresh = async () => {
    setSelectedTicketId(null);
    // Refresh data when returning from ticket details with changes
    if (selectedTab === 'tickets-requests') {
      await fetchPendingTicketsData();
    } else if (selectedTab === 'tickets-closed') {
      await fetchClosedTicketsData();
    }
  };

  const truncateText = (text, maxLength = 20) => {
    if (text.length <= maxLength) return text;
    const start = Math.floor(maxLength / 2) - 2;
    const end = Math.floor(maxLength / 2) + 2;
    return text.substring(0, start) + '...' + text.substring(text.length - end);
  };

  // Show ticket details if a ticket is selected
  if (selectedTicketId) {
    return <TicketDetails
      ticketId={selectedTicketId}
      onClose={dataChanged ? handleCloseTicketDetailsWithRefresh : handleCloseTicketDetails}
      onDataChange={(ticketId) => {
        // Remove the ticket from local state immediately
        if (selectedTab === 'tickets-requests') {
          setPendingTickets(prev => prev.filter(ticket => ticket.id !== ticketId));
        } else if (selectedTab === 'tickets-closed') {
          setClosedTickets(prev => prev.filter(ticket => ticket.id !== ticketId));
        }
        setDataChanged(true);
      }}
    />;
  }

  // Render CategoriesTabContent for categories sub-tab
  if (selectedTab === 'tickets-categories') {
    return <CategoriesTabContent />;
  }

  // Render dropdown menu using portal
  const renderDropdownMenu = () => {
    if (!openDropdownId) return null;

    const currentItem = filteredData.find(item => item.id === openDropdownId);
    if (!currentItem) return null;

    // Ensure dropdown doesn't go off-screen
    const viewportWidth = window.innerWidth;
    const dropdownWidth = 160;
    let left = dropdownPosition.left;

    // If dropdown would go off the right edge, align it to the left of the button
    if (left + dropdownWidth > viewportWidth) {
      left = dropdownPosition.left - dropdownWidth + 32; // 32px is button width
    }

    return createPortal(
      <div
        className={styles.dropdownMenu}
        data-dropdown-menu
        style={{
          position: 'fixed',
          top: dropdownPosition.top,
          left: left,
          zIndex: 99999
        }}
      >
        {hasCloseTicket && (
          <button
            className={styles.dropdownItem}
            onClick={(e) => {
              e.stopPropagation();
              handleCloseTicket(currentItem.id);
            }}
            disabled={actionLoading}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 12l2 2 4-4" />
              <path d="M21 12c-1 0-2.4-.4-3.5-1.5S16 8 16 7s.4-2.4 1.5-3.5S20 2 21 2s2.4.4 3.5 1.5S26 6 26 7s-.4 2.4-1.5 3.5S22 12 21 12z" />
            </svg>
            Close Ticket
          </button>
        )}
        {hasDeleteTicket && (
          <button
            className={`${styles.dropdownItem} ${styles.deleteItem}`}
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteTicket(currentItem.id);
            }}
            disabled={actionLoading}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3,6 5,6 21,6" />
              <path d="M19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2" />
              <line x1="10" y1="11" x2="10" y2="17" />
              <line x1="14" y1="11" x2="14" y2="17" />
            </svg>
            Delete Ticket
          </button>
        )}
      </div>,
      document.body
    );
  };

  return (
    <div className={styles.ticketsContainer}>
      <div className={styles.headerContainer}>
        <div className={styles.headerLeft}>
          <h2 className={styles.headerTitle}>Tickets</h2>
        </div>
        <div className={styles.headerActions}>
          <button className={styles.actionButton} onClick={handleFilter}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="22,3 2,3 10,12.46 10,19 14,21 14,12.46" />
            </svg>
            Filter
          </button>
          {hasActiveFilters() && (
            <button className={styles.actionButton} onClick={handleResetFilter}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
                <path d="M21 3v5h-5" />
                <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
                <path d="M3 21v-5h5" />
              </svg>
              Reset
            </button>
          )}
          {hasExportTicket && (
            <button className={styles.actionButton} onClick={handleExport} disabled={exportLoading}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7,10 12,15 17,10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              {exportLoading ? 'Exporting...' : 'Export'}
            </button>
          )}
        </div>
      </div>

      {selectedTab === 'tickets' ? (
        <div className={styles.selectSubTabMessage}>
          <h3>Select Ticket Type</h3>
          <p>Please select "Requests", "Closed", or "Categories" to view tickets.</p>
        </div>
      ) : (selectedTab === 'tickets-requests' || selectedTab === 'tickets-closed') && loading ? (
        <div className={styles.loadingMessage}>
          <h3>Loading...</h3>
          <p>Fetching {selectedTab === 'tickets-requests' ? 'pending' : 'closed'} tickets...</p>
        </div>
      ) : (selectedTab === 'tickets-requests' || selectedTab === 'tickets-closed') && error ? (
        <div className={styles.errorMessage}>
          <h3>Error</h3>
          <p>{error}</p>
          <button onClick={() => selectedTab === 'tickets-requests' ? fetchPendingTicketsData(1) : fetchClosedTicketsData(1)} className={styles.retryButton}>
            Retry
          </button>
        </div>
      ) : filteredData.length > 0 ? (
        <>
          <div className={styles.tableContainer}>
            <table className={styles.ticketsTable}>
              <thead>
                <tr>
                  <th className={styles.idColumn}>ID</th>
                  <th className={styles.statusColumn}>Status</th>
                  <th className={styles.categoryColumn}>Category</th>
                  <th className={styles.customerColumn}>Customer</th>
                  <th className={styles.descriptionColumn}>Description</th>
                  <th className={styles.dateColumn}>Date</th>
                  <th className={styles.actionsColumn}></th>
                </tr>
              </thead>
              <tbody>
                {filteredData.map((item, index) => (
                  <React.Fragment key={item.id}>
                    <tr
                      className={styles.tableRow}
                      onClick={() => handleTicketClick(item.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td className={styles.idColumn}>
                        <span className={styles.idValue}>#{((currentPage - 1) * ITEMS_PER_PAGE) + index + 1}</span>
                      </td>
                      <td className={styles.statusColumn}>
                        <span className={`${styles.statusBadge} ${styles[item.status]}`}>
                          {item.status === 'pending' ? 'Pending' : 'Resolved'}
                        </span>
                      </td>
                      <td className={styles.categoryColumn}>
                        <span className={styles.categoryText}>{item.category || 'N/A'}</span>
                      </td>
                      <td className={styles.customerColumn}>
                        <span className={styles.customerName}>{item.customer}</span>
                      </td>
                      <td className={styles.descriptionColumn}>
                        <span className={styles.descriptionText}>{truncateText(item.description)}</span>
                      </td>
                      <td className={styles.dateColumn}>
                        <span className={styles.dateValue}>
                          {item.date}
                        </span>
                      </td>
                      <td className={styles.actionsColumn}>
                        <div className={styles.rowActions}>
                          {selectedTab === 'tickets-closed' ? (
                            // For closed tickets, show only delete button
                            hasDeleteTicket && (
                              <button
                                className={`${styles.actionMenuButton} ${styles.deleteButton}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteTicket(item.id);
                                }}
                                title="Delete"
                                disabled={actionLoading}
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="3,6 5,6 21,6" />
                                  <path d="m19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2" />
                                  <line x1="10" y1="11" x2="10" y2="17" />
                                  <line x1="14" y1="11" x2="14" y2="17" />
                                </svg>
                              </button>
                            )
                          ) : (
                            // For requested tickets, show dropdown menu only if user has any action permissions
                            (hasCloseTicket || hasDeleteTicket) && (
                              <div className={styles.dropdownContainer}>
                                <button
                                  className={styles.actionMenuButton}
                                  onClick={(e) => handleDropdownToggle(e, item.id)}
                                  title="Actions"
                                  disabled={actionLoading}
                                >
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="12" cy="12" r="1" />
                                    <circle cx="19" cy="12" r="1" />
                                    <circle cx="5" cy="12" r="1" />
                                  </svg>
                                </button>
                              </div>
                            )
                          )}
                        </div>
                      </td>
                    </tr>
                    {index < filteredData.length - 1 && (
                      <tr>
                        <td colSpan="8" className={styles.dividerRow}>
                          <div className={styles.divider}></div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
          {pagination.last_page > 1 && (
            <PageNavigator
              currentPage={currentPage}
              totalPages={pagination.last_page}
              onPageChange={handlePageChange}
            />
          )}
        </>
      ) : (
        <div className={styles.noTicketsMessage}>
          <h3>No {selectedTab === 'tickets-requests' ? 'Requests' : 'Closed'} Tickets</h3>
          <p>There are currently no {selectedTab === 'tickets-requests' ? 'request' : 'closed'} tickets to display.</p>
        </div>
      )}
      {renderDropdownMenu()}

      {/* Filter Dialog */}
      {showFilterDialog && (
        <FilterDialog
          isOpen={showFilterDialog}
          onClose={handleFilterClose}
          onApply={handleFilterApply}
          title="Filter Tickets"
          fields={filterFields}
          initialValues={filterValues}
        />
      )}
    </div>
  );
};

export default TicketsTabContent;